import { map } from 'lodash';

import { SUBMITTABLE_EXPERIMENTAL_STRATEGIES } from 'constants/constants';
import { fetchGraphql, fetchQC } from '../utils/ajax';

const QC_STATUS_REQUEST = 'gdc/QC_STATUS_REQUEST';
const QC_STATUS_SUCCESS = 'gdc/QC_STATUS_SUCCESS';
const QC_STATUS_FAILURE = 'gdc/QC_STATUS_FAILURE';

const QC_SUMMARY_REQUEST = 'gdc/QC_DETAIL_SUMMARY_REQUEST';
const QC_SUMMARY_SUCCESS = 'gdc/QC_DETAIL_SUMMARY_SUCCESS';
const QC_SUMMARY_FAILURE = 'gdc/QC_DETAIL_SUMMARY_FAILURE';

const QC_DETAIL_SUMMARY_REQUEST = 'gdc/QC_DETAIL_SUMMARY_REQUEST';
const QC_DETAIL_SUMMARY_SUCCESS = 'gdc/QC_DETAIL_SUMMARY_SUCCESS';
const QC_DETAIL_SUMMARY_FAILURE = 'gdc/QC_DETAIL_SUMMARY_FAILURE';

const QC_FILE_DOWNLOAD_REQUEST = 'gdc/QC_FILE_DOWNLOAD_REQUEST';
const QC_FILE_DOWNLOAD_SUCCESS = 'gdc/QC_FILE_DOWNLOAD_SUCCESS';
const QC_FILE_DOWNLOAD_FAILURE = 'gdc/QC_FILE_DOWNLOAD_FAILURE';

const QC_NODE_TYPE_DETAIL_REQUEST = 'gdc/QC_NODE_TYPE_DETAIL_REQUEST';
const QC_NODE_TYPE_DETAIL_SUCCESS = 'gdc/QC_NODE_TYPE_DETAIL_SUCCESS';
const QC_NODE_TYPE_DETAIL_FAILURE = 'gdc/QC_NODE_TYPE_DETAIL_FAILURE';

const QC_METADATA_REQUEST = 'gdc/QC_METADATA_REQUEST';
const QC_METADATA_SUCCESS = 'gdc/QC_METADATA_SUCCESS';
const QC_METADATA_FAILURE = 'gdc/QC_METADATA_FAILURE';

export const fetchQCStatus = ({ program, project }) => fetchQC({
  endpoint: 'status',
  program,
  project,
  types: [
    QC_STATUS_REQUEST,
    QC_STATUS_SUCCESS,
    QC_STATUS_FAILURE,
  ],
});

export const fetchQCSummary = ({ program, project }) => fetchQC({
  endpoint: 'summary',
  program,
  project,
  types: [
    QC_SUMMARY_REQUEST,
    QC_SUMMARY_SUCCESS,
    QC_SUMMARY_FAILURE,
  ],
});

export const fetchQCDetailSummary = ({ program, project }) => fetchQC({
  endpoint: 'detail',
  program,
  project,
  types: [
    QC_DETAIL_SUMMARY_REQUEST,
    QC_DETAIL_SUMMARY_SUCCESS,
    QC_DETAIL_SUMMARY_FAILURE,
  ],
});

export const fetchQCNodeTypeDetail = ({
  entity: nodeType = 'case',
  params = {},
  program,
  project,
}) => fetchQC({
  format: params.format,
  endpoint: `detail/${nodeType}`,
  params,
  program,
  project,
  types: params.format
    ? QC_FILE_DOWNLOAD_REQUEST
    : [
      QC_NODE_TYPE_DETAIL_REQUEST,
      QC_NODE_TYPE_DETAIL_SUCCESS,
      QC_NODE_TYPE_DETAIL_FAILURE,
    ],
});

export const handleQCDownload = (dispatch, promise) => promise
  .then(() => dispatch({ type: QC_FILE_DOWNLOAD_SUCCESS }))
  .catch(() => dispatch({ type: QC_FILE_DOWNLOAD_FAILURE }));

const queryQCMetadata = ({ projectId }) => ({
  variables: {
    projectId,
  },
  query: `query QCMetadataQuery (
      $projectId: [String]
    ) {
      caseCount: _case_count (state: ["validated"], project_id: $projectId)
      aliquotCount: _aliquot_count(state: ["validated"], project_id: $projectId)
      readGroupCount: _read_group_count(state: ["validated"], project_id: $projectId)
      ${map(SUBMITTABLE_EXPERIMENTAL_STRATEGIES, (value, key) => `SAR_${key}Count: _submitted_aligned_reads_count (state: ["validated"], project_id: $projectId experimental_strategy: ["${value}"])`).join(' ')}
      ${map(SUBMITTABLE_EXPERIMENTAL_STRATEGIES, (value, key) => `SUR_${key}Count: _submitted_unaligned_reads_count (state: ["validated"], project_id: $projectId experimental_strategy: ["${value}"])`).join(' ')}
    }`,
});

export const fetchQCMetadata = ({ projectId }) => fetchGraphql(
  [
    QC_METADATA_REQUEST,
    QC_METADATA_SUCCESS,
    QC_METADATA_FAILURE,
  ],
  queryQCMetadata({ projectId }),
);

const initialState = {
  statusData: {
    data: {},
    pending: null,
    status: null,
    isFetching: false,
  },
  summaryData: {
    data: {},
    isFetching: false,
  },
  nodeTypeData: {
    data: {
      count: 0,
      summary: {},
      items: [],
    },
    isDownloading: false,
    isFetching: false,
  },
  metadata: {
    data: {},
    isFetching: false,
  },
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case QC_STATUS_REQUEST:
      return {
        ...state,
        statusData: {
          ...state.summaryData,
          isFetching: true,
        },
      };
    case QC_STATUS_SUCCESS: {
      const {
        checks_pending,
        data,
        status,
      } = action.payload;

      return {
        ...state,
        statusData: {
          data: {
            items: map(data, (info, severity) => ({
              severity,
                // description: info.description,
              count: info,
            })),
          },
          pending: checks_pending,
          status,
          isFetching: false,
        },
      };
    }
    case QC_STATUS_FAILURE:
      return {
        ...state,
        statusData: {
          ...state.summaryData,
          isFetching: false,
        },
      };
    case QC_SUMMARY_REQUEST:
      return {
        ...state,
        summaryData: {
          ...state.summaryData,
          isFetching: true,
        },
      };
    case QC_SUMMARY_SUCCESS:
      return {
        ...state,
        summaryData: {
          data: {
            items: map(action.payload.data, (info, severity) => ({
              severity,
                // description: info.description,
              count: info,
            })),
          },
          isFetching: false,
        },
      };
    case QC_SUMMARY_FAILURE:
      return {
        ...state,
        summaryData: {
          ...state.summaryData,
          isFetching: false,
        },
      };
    case QC_DETAIL_SUMMARY_REQUEST:
      return {
        ...state,
        summaryData: {
          ...state.summaryData,
          isFetching: true,
        },
      };
    case QC_DETAIL_SUMMARY_SUCCESS:
      return {
        ...state,
        summaryData: {
          data: {
            items: map(action.payload.summary, (info, errorType) => ({
              errorType, severity: info.severity, description: info.description, count: info.count,
            })),
          },
          isFetching: false,
        },
      };
    case QC_DETAIL_SUMMARY_FAILURE:
      return {
        ...state,
        summaryData: {
          ...state.summaryData,
          isFetching: false,
        },
      };
    case QC_NODE_TYPE_DETAIL_REQUEST:
      return {
        ...state,
        nodeTypeData: {
          ...state.nodeTypeData,
          isFetching: true,
        },
      };
    case QC_NODE_TYPE_DETAIL_SUCCESS:
      return {
        ...state,
        nodeTypeData: {
          data: {
            count: action.payload
              ? action.payload.data.count
              : 0,
            summary: action.payload
              ? action.payload.summary
              : {},
            items: action.payload
              ? action.payload.data.items
              : [],
          },
          isDownloading: false,
          isFetching: false,
        },
      };
    case QC_NODE_TYPE_DETAIL_FAILURE:
      return {
        ...state,
        nodeTypeData: {
          ...state.nodeTypeData,
          isFetching: false,
        },
      };
    case QC_METADATA_REQUEST:
      return {
        ...state,
        metadata: {
          ...state.metadata,
          isFetching: true,
        },
      };
    case QC_METADATA_SUCCESS:
      return {
        ...state,
        metadata: {
          data: action.payload.data,
          isFetching: false,
        },
      };
    case QC_METADATA_FAILURE:
      return {
        ...state,
        metadata: {
          ...state.metadata,
          isFetching: false,
        },
      };
    case QC_FILE_DOWNLOAD_REQUEST:
      return {
        ...state,
        nodeTypeData: {
          ...state.nodeTypeData,
          isDownloading: true,
        },
      };
    case QC_FILE_DOWNLOAD_SUCCESS:
    case QC_FILE_DOWNLOAD_FAILURE:
      return {
        ...state,
        nodeTypeData: {
          ...state.nodeTypeData,
          isDownloading: false,
        },
      };
    default:
      return state;
  }
}
